import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Check, Loader2, ArrowRight } from "lucide-react";
import TreeMark from "@/components/TreeMark";

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 10;

export default function UpgradeSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const paypalOrderId = params.get("paypal_order_id");
  const { user } = useAuth();
  const navigate = useNavigate();

  const [state, setState] = useState({ status: "polling", attempts: 0, data: null, error: "" });

  useEffect(() => {
    // PayPal: capture happens inline before redirect, so is_premium should already be true.
    if (paypalOrderId) {
      (async () => {
        try {
          const { data } = await api.get("/auth/me");
          if (data.is_premium) {
            setState({ status: "paid", data: { payment_status: "paid", provider: "paypal" } });
            // Redirect out of this URL so its query params can't re-trigger anything.
            setTimeout(() => { window.location.href = "/dashboard"; }, 1500);
          } else {
            setState({ status: "timeout" });
          }
        } catch (e) {
          setState({ status: "error", error: formatApiError(e) });
        }
      })();
      return;
    }
    if (!sessionId) {
      setState({ status: "error", error: "No session id in URL." });
      return;
    }
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ status: "paid", data });
          // Refresh user in auth context via /me so the app knows we're premium
          try { await api.get("/auth/me"); } catch (e) { console.error("Failed to refresh auth after payment:", e); }
          // Redirect to dashboard after showing the success message briefly.
          // (No reload — reloading keeps ?session_id= in the URL and re-triggers this effect.)
          setTimeout(() => { window.location.href = "/dashboard"; }, 1500);
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", data });
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          setState({ status: "timeout", data });
          return;
        }
        setState((s) => ({ ...s, status: "polling", attempts, data }));
        setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        if (cancelled) return;
        setState({ status: "error", error: formatApiError(e) });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, paypalOrderId]);

  return (
    <div className="max-w-2xl mx-auto px-6 md:px-12 py-20 md:py-28 text-center">
      <TreeMark size={64} tone="dark" className="mb-6 mx-auto" />

      {state.status === "polling" && (
        <>
          <Loader2 className="mx-auto mb-6 text-[#6B7F62] animate-spin" size={32} />
          <h1 className="font-serif text-3xl md:text-4xl text-[#2D332D] mb-3">Just a moment…</h1>
          <p className="text-[#576056] leading-relaxed">
            Confirming your payment. This normally takes a few seconds.
          </p>
        </>
      )}

      {state.status === "paid" && (
        <>
          <div className="w-16 h-16 rounded-full bg-[#E9ECE8] flex items-center justify-center text-[#6B7F62] mx-auto mb-6">
            <Check size={28} strokeWidth={2.25} />
          </div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#6B7F62] mb-3">Thank you</div>
          <h1 data-testid="payment-success-heading" className="font-serif text-4xl md:text-5xl text-[#2D332D] mb-4">
            You're in.
          </h1>
          <p className="text-[#576056] text-lg leading-relaxed mb-8">
            Your lifetime access is unlocked, {user?.name || "friend"}. Take your time — start when the day feels kind.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/plan" className="btn-primary" data-testid="post-payment-plan">Open Plan Builder <ArrowRight size={17} /></Link>
            <Link to="/guide/prepare" className="btn-secondary">Read the guide</Link>
          </div>
        </>
      )}

      {state.status === "expired" && (
        <>
          <h1 className="font-serif text-3xl text-[#2D332D] mb-3">The checkout expired.</h1>
          <p className="text-[#576056] mb-6">No charge was made. You can try again whenever you're ready.</p>
          <Link to="/upgrade" className="btn-primary">Try again</Link>
        </>
      )}

      {state.status === "timeout" && (
        <>
          <h1 className="font-serif text-3xl text-[#2D332D] mb-3">Still processing…</h1>
          <p className="text-[#576056] mb-6">Payment is confirmed by Stripe within a minute or two. Please refresh, or head to your dashboard — access will unlock automatically.</p>
          <Link to="/dashboard" className="btn-primary">Go to dashboard</Link>
        </>
      )}

      {state.status === "error" && (
        <>
          <h1 className="font-serif text-3xl text-[#2D332D] mb-3">Something went wrong.</h1>
          <p className="text-[#B05A5A] mb-6">{state.error}</p>
          <Link to="/upgrade" className="btn-primary">Back to upgrade</Link>
        </>
      )}
    </div>
  );
}
